/**
 * Data type definitions for the vehicle spec hub.
 * Each data type maps to a page at /{make}/{model}/{dataType}.
 */

export const DATA_TYPES = {
  mpg: {
    slug: "mpg",
    label: "Fuel Economy (MPG)",
    shortLabel: "MPG",
    title: "MPG & Fuel Economy",
    description: (make: string, model: string) =>
      `${make} ${model} fuel economy — city, highway, and combined MPG for every model year, engine, and trim.`,
    jsonLdType: "Dataset",
  },
  engine: {
    slug: "engine",
    label: "Engine Specifications",
    shortLabel: "Engine Specs",
    title: "Engine Specifications",
    description: (make: string, model: string) =>
      `${make} ${model} engine specs — cylinders, displacement, transmission, and drivetrain for every model year and trim.`,
    jsonLdType: "Dataset",
  },
  "fuel-cost": {
    slug: "fuel-cost",
    label: "Annual Fuel Cost",
    shortLabel: "Fuel Cost",
    title: "Annual Fuel Cost",
    description: (make: string, model: string) =>
      `${make} ${model} annual fuel cost — estimated yearly fuel spending and CO₂ emissions for every model year and engine.`,
    jsonLdType: "Dataset",
  },
  transmission: {
    slug: "transmission",
    label: "Transmission",
    shortLabel: "Transmission",
    title: "Transmission Specifications",
    description: (make: string, model: string) =>
      `${make} ${model} transmission specs — automatic, manual, CVT, and gear counts by model year, engine, and trim.`,
    jsonLdType: "Dataset",
  },
  drivetrain: {
    slug: "drivetrain",
    label: "Drivetrain (FWD/RWD/AWD/4WD)",
    shortLabel: "Drivetrain",
    title: "Drivetrain & Drive Type",
    description: (make: string, model: string) =>
      `${make} ${model} drivetrain options — front-wheel, rear-wheel, all-wheel, and 4WD availability by model year and trim.`,
    jsonLdType: "Dataset",
  },
  "vehicle-class": {
    slug: "vehicle-class",
    label: "Vehicle Class & Size",
    shortLabel: "Vehicle Class",
    title: "Vehicle Class & EPA Size Category",
    description: (make: string, model: string) =>
      `${make} ${model} EPA vehicle class — official size category, body type, and how it is classified by model year.`,
    jsonLdType: "Dataset",
  },
  "maintenance-schedule": {
    slug: "maintenance-schedule",
    label: "Maintenance Schedule",
    shortLabel: "Maintenance",
    title: "Maintenance Schedule & Service Intervals",
    description: (make: string, model: string) =>
      `${make} ${model} maintenance schedule — oil change, tire rotation, fluid, filter, and brake service intervals by mileage.`,
    jsonLdType: "Dataset",
  },
  "towing-capacity": {
    slug: "towing-capacity",
    label: "Towing Capacity",
    shortLabel: "Towing",
    title: "Towing Capacity",
    description: (make: string, model: string) =>
      `${make} ${model} towing capacity — max trailer weight, payload, and GCWR by model year, engine, and trim.`,
    jsonLdType: "Dataset",
  },
  "tire-size": {
    slug: "tire-size",
    label: "Tire Size",
    shortLabel: "Tire Size",
    title: "Tire Size & Wheel Fitment",
    description: (make: string, model: string) =>
      `${make} ${model} tire sizes — OEM front and rear tire sizes, wheel diameter, and recommended tire pressure by year and trim.`,
    jsonLdType: "Dataset",
  },
  "bolt-pattern": {
    slug: "bolt-pattern",
    label: "Bolt Pattern & Wheel Fitment",
    shortLabel: "Bolt Pattern",
    title: "Bolt Pattern & Wheel Specs",
    description: (make: string, model: string) =>
      `${make} ${model} bolt pattern (PCD), center bore, thread size, and lug nut torque by model year.`,
    jsonLdType: "Dataset",
  },
  dimensions: {
    slug: "dimensions",
    label: "Dimensions & Size",
    shortLabel: "Dimensions",
    title: "Dimensions & Size",
    description: (make: string, model: string) =>
      `${make} ${model} dimensions — length, width, height, wheelbase, and curb weight by model year. Check whether it fits your garage or parking space.`,
    jsonLdType: "Dataset",
  },
  "safety-ratings": {
    slug: "safety-ratings",
    label: "Safety Ratings",
    shortLabel: "Safety",
    title: "NHTSA Safety Ratings",
    description: (make: string, model: string) =>
      `${make} ${model} NHTSA safety ratings — overall, frontal crash, side crash, and rollover star ratings by model year.`,
    jsonLdType: "Dataset",
  },
  "ev-range": {
    slug: "ev-range",
    label: "EV Range & Charging",
    shortLabel: "EV Range",
    title: "Electric Range & Charging Time",
    description: (make: string, model: string) =>
      `${make} ${model} electric range and charging times — EPA range, motor output, and Level 1 / Level 2 charge hours by model year.`,
    jsonLdType: "Dataset",
  },
  "bed-dimensions": {
    slug: "bed-dimensions",
    label: "Truck Bed Dimensions",
    shortLabel: "Bed Size",
    title: "Truck Bed Dimensions",
    description: (make: string, model: string) =>
      `${make} ${model} truck bed dimensions — bed length, width, width between wheel wells, depth, and cargo volume for every bed configuration.`,
    jsonLdType: "Dataset",
  },
  "fuel-tank-capacity": {
    slug: "fuel-tank-capacity",
    label: "Fuel Tank Capacity",
    shortLabel: "Gas Tank",
    title: "Fuel Tank Capacity",
    description: (make: string, model: string) =>
      `${make} ${model} gas tank size — fuel tank capacity in gallons by model year, plus estimated driving range on a full tank.`,
    jsonLdType: "Dataset",
  },
  "curb-weight": {
    slug: "curb-weight",
    label: "Curb Weight",
    shortLabel: "Curb Weight",
    title: "Curb Weight",
    description: (make: string, model: string) =>
      `${make} ${model} curb weight — how much it weighs empty, by model year and body style, with gross weight context.`,
    jsonLdType: "Dataset",
  },
} as const;

export type DataTypeKey = keyof typeof DATA_TYPES;

export const VALID_DATA_TYPES = Object.keys(DATA_TYPES) as string[];

export function isValidDataType(dt: string): dt is DataTypeKey {
  return dt in DATA_TYPES;
}

export function getDataTypeInfo(dt: DataTypeKey) {
  return DATA_TYPES[dt];
}

/**
 * All available data types for the current vehicle, used for cross-linking.
 */
export const ALL_DATA_TYPES: DataTypeKey[] = [
  "mpg",
  "engine",
  "fuel-cost",
  "transmission",
  "drivetrain",
  "vehicle-class",
  "maintenance-schedule",
];

/**
 * "Money" data types. These come from manufacturer specs stored in the
 * vehicle_specs table, not from the free government feeds. A page only
 * exists (and is only listed in the sitemap) when verified data is present.
 */
export const SPEC_DATA_TYPES = [
  "towing-capacity",
  "tire-size",
  "bolt-pattern",
  "bed-dimensions",
  "fuel-tank-capacity",
] as const;

export type SpecDataTypeKey = (typeof SPEC_DATA_TYPES)[number];

export function isSpecDataType(dt: string): dt is SpecDataTypeKey {
  return (SPEC_DATA_TYPES as readonly string[]).includes(dt);
}

/**
 * Data types sourced from free NHTSA feeds. Like the money pages, a page only
 * exists once real rows are present, so nothing thin ever ships.
 */
export const NHTSA_DATA_TYPES = ["dimensions", "safety-ratings", "curb-weight"] as const;

export type NhtsaDataTypeKey = (typeof NHTSA_DATA_TYPES)[number];

export function isNhtsaDataType(dt: string): dt is NhtsaDataTypeKey {
  return (NHTSA_DATA_TYPES as readonly string[]).includes(dt);
}

/** Only meaningful for electric and plug-in hybrid models. */
export const EV_DATA_TYPE = "ev-range";

/**
 * Every data type whose page is published conditionally on real data being
 * present, rather than always available from the EPA feed.
 */
export const CONDITIONAL_DATA_TYPES: string[] = [
  ...SPEC_DATA_TYPES,
  ...NHTSA_DATA_TYPES,
  EV_DATA_TYPE,
];

export function isConditionalDataType(dt: string): boolean {
  return CONDITIONAL_DATA_TYPES.includes(dt);
}

/** Slug list for sitemap and URL generation (EPA-derived types, always available). */
export const ALL_DATA_TYPE_SLUGS: string[] = ALL_DATA_TYPES.map((k) => DATA_TYPES[k].slug);
